import React, { useState } from 'react';
import {
  Card,
  Button,
  Form,
  Alert,
  Badge,
  Collapse,
  Spinner,
  ProgressBar,
} from 'react-bootstrap';
import ServiceOfferService from '../../../Services/ServiceOfferService';
import EmailInboxPanel from '../../../Components/EmailInboxPanel';

/**
 * AI Extract Panel for Service Offers
 * Allows users to paste text describing service needs and extract structured offer data using AI
 */
function AIExtractPanel({ onApplyExtraction }) {
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [showInbox, setShowInbox] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleExtract = async () => {
    if (!text || text.length < 20) {
      setError('Please paste more text to extract offer details');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await ServiceOfferService.aiExtract(text);
      const data = response?.data?.data;

      if (data?.success) {
        setResult(data);
      } else {
        setError('Failed to extract offer details. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to extract offer details');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (result?.offer) {
      onApplyExtraction(result.offer);
      setIsOpen(false);
      setText('');
      setResult(null);
    }
  };

  const getConfidenceVariant = (confidence) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.5) return 'warning';
    return 'danger';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.8) return 'High';
    if (confidence >= 0.5) return 'Medium';
    return 'Low';
  };

  return (
    <Card className="mb-3 ai-extract-panel">
      <Card.Header
        onClick={() => setIsOpen(!isOpen)}
        className="ai-panel-header d-flex justify-content-between align-items-center"
      >
        <div className="d-flex align-items-center gap-2 text-white">
          <span className="ai-icon">&#129302;</span>
          <span className="ai-title">AI Offer Generator</span>
        </div>
        <Badge className="ai-badge text-white" pill>
          {isOpen ? '\u25B2 Collapse' : '\u25BC Expand'}
        </Badge>
      </Card.Header>
      <Collapse in={isOpen}>
        <Card.Body className="ai-panel-body">
          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="fw-semibold text-dark mb-0">
                <i className="fa fa-paste me-2 text-primary" />
                Describe the service offer in natural language
              </Form.Label>
              <div className="btn-group btn-group-sm">
                <Button
                  variant={!showInbox ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setShowInbox(false)}
                >
                  <i className="fa fa-keyboard-o me-1" />
                  Paste text
                </Button>
                <Button
                  variant={showInbox ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setShowInbox(true)}
                >
                  <i className="fa fa-inbox me-1" />
                  From inbox
                </Button>
              </div>
            </div>
            {showInbox ? (
              <EmailInboxPanel
                onSelect={(html) => {
                  setText(html);
                  setShowInbox(false);
                }}
              />
            ) : (
              <>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Describe the transportation services needed. For example: 'We need cars and vans for a UEFA event in Budapest, May 29-31. Arrival and departure days need 8 hours, match day needs 10 hours. Sedan rate 1500 EUR, van rate 1500 EUR, sprinter 1650 EUR. Quote for Kévin Decerle from VPI Agency.'"
                  disabled={loading}
                />
                <Form.Text className="text-muted">
                  Paste a client request, email, or describe the services
                  needed. The AI will structure it into an offer.
                </Form.Text>
              </>
            )}
          </Form.Group>

          <div className="d-flex gap-2 mb-3">
            <Button
              variant="primary"
              onClick={handleExtract}
              disabled={loading || !text || text.length < 20}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Extracting...
                </>
              ) : (
                <>
                  <i className="fa fa-magic me-2" />
                  Generate Offer
                </>
              )}
            </Button>
            {text && (
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setText('');
                  setResult(null);
                  setError('');
                }}
                disabled={loading}
              >
                Clear
              </Button>
            )}
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {result && (
            <div className="ai-results">
              {/* Confidence */}
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small className="fw-bold">
                    Confidence:
                    {' '}
                    {getConfidenceLabel(result.confidence)}
                    {' '}
                    (
                    {Math.round(result.confidence * 100)}
                    %)
                  </small>
                </div>
                <ProgressBar
                  now={result.confidence * 100}
                  variant={getConfidenceVariant(result.confidence)}
                />
              </div>

              {/* Warnings */}
              {result.warnings && result.warnings.length > 0 && (
                <Alert variant="warning" className="py-2">
                  <small>
                    <i className="fa fa-exclamation-triangle me-2" />
                    {result.warnings.join(' | ')}
                  </small>
                </Alert>
              )}

              {/* Preview of extracted data */}
              <Card className="mb-3">
                <Card.Body className="py-2">
                  <div className="row">
                    <div className="col-md-6">
                      <small className="text-muted">Title</small>
                      <p className="mb-2 fw-semibold">{result.offer?.title || '-'}</p>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Recipient</small>
                      <p className="mb-2 fw-semibold">
                        {result.offer?.recipient?.name || '-'}
                        {result.offer?.recipient?.company && (
                          <span className="text-muted fw-normal">
                            {' '}
                            (
                            {result.offer.recipient.company}
                            )
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <small className="text-muted">
                        Service Tables:
                        {' '}
                        {result.offer?.serviceTables?.length || 0}
                      </small>
                      {result.offer?.serviceTables?.map((table) => (
                        <div key={table.title} className="ms-2">
                          <small className="fw-semibold">
                            {table.title}
                            {' '}
                            (
                            {table.rows?.length || 0}
                            {' '}
                            rows)
                          </small>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-md-6">
                      <small className="text-muted">
                        Inclusions:
                        {' '}
                        {result.offer?.inclusions?.length || 0}
                      </small>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">
                        Exclusions:
                        {' '}
                        {result.offer?.exclusions?.length || 0}
                      </small>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Apply button */}
              <Button
                variant="success"
                onClick={handleApply}
                className="w-100"
              >
                <i className="fa fa-check me-2" />
                Apply to Form
              </Button>
            </div>
          )}
        </Card.Body>
      </Collapse>
    </Card>
  );
}

export default AIExtractPanel;
